import type {
  DayCell,
  RangeDay,
  RangeSnapshot,
  RangeUser,
} from '~/lib/api/types'

const HEADERS = [
  'date',
  'slack_user_id',
  'display_name',
  'status',
  'hours_start',
  'hours_end',
  'total_hours',
  'mood',
  'plan_excerpt',
  'time_off_kind',
  'note',
] as const

/**
 * Build a CSV string for the visible range. Rows are ordered by
 * date ascending then engineer display name. Holidays produce one
 * row per engineer with status "holiday" so the CSV is dense and
 * easy to pivot.
 */
export function rangeSnapshotToCsv(snapshot: RangeSnapshot): string {
  const rows: string[][] = [HEADERS.slice()]
  const usersById = new Map(snapshot.users.map(u => [u.id, u]))

  for (const day of snapshot.days) {
    const dayRows = buildDayRows(day, usersById, snapshot.users)
    for (const row of dayRows) rows.push(row)
  }

  return rows.map(row => row.map(escapeField).join(',')).join('\n')
}

export function csvFilenameFor(snapshot: RangeSnapshot): string {
  return `politech-checkins-${snapshot.from}_to_${snapshot.to}.csv`
}

/**
 * Trigger a browser download of `csv` named `filename`. Returns
 * the URL it created so callers can revoke it. Pure side-effect on
 * `document` and `URL`.
 */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

function buildDayRows(
  day: RangeDay,
  usersById: Map<string, RangeUser>,
  users: RangeUser[]
): string[][] {
  if (day.is_holiday) {
    return users.map(user => [
      day.date,
      user.slack_user_id,
      user.display_name ?? '',
      'holiday',
      '',
      '',
      '',
      '',
      '',
      '',
      day.holiday?.name ?? '',
    ])
  }

  return day.cells.map(cell => cellRow(day.date, cell, usersById))
}

function cellRow(
  date: string,
  cell: DayCell,
  usersById: Map<string, RangeUser>
): string[] {
  const user = usersById.get(cell.user_id)
  const slackId = user?.slack_user_id ?? ''
  const displayName = user?.display_name ?? ''

  if (cell.kind === 'working') {
    const start = cell.hours_parsed?.start ?? ''
    const end = cell.hours_parsed?.end ?? ''
    const total = cell.hours_parsed?.total_hours
    return [
      date,
      slackId,
      displayName,
      'working',
      start,
      end,
      total != null ? String(total) : '',
      cell.mood ?? '',
      cell.plan_excerpt ?? '',
      '',
      cell.mood_note ?? '',
    ]
  }

  if (cell.kind === 'parse_failed') {
    return [
      date,
      slackId,
      displayName,
      'parse_failed',
      '',
      '',
      '',
      cell.mood ?? '',
      '',
      '',
      cell.hours_raw ?? '',
    ]
  }

  if (cell.kind === 'ooo') {
    return [
      date,
      slackId,
      displayName,
      'ooo',
      '',
      '',
      '',
      '',
      '',
      cell.ooo_kind,
      cell.note ?? '',
    ]
  }

  return [date, slackId, displayName, 'missing', '', '', '', '', '', '', '']
}

function escapeField(value: string): string {
  if (value === '') return ''
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}
